import React, { useMemo } from 'react';
import './CustomListRecordPage.scss';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Col, Form as ReactForm, Table, Row } from 'react-bootstrap';
import { Button, DatePicker, SalvButton, SalvDropdown, SalvTextArea, Size } from '@salvapp/ui-components';
import {
  InternalApiCustomList,
  InternalApiCustomListRecord,
  InternalApiCustomListSchemaField,
  InternalApiCustomListSchemaFieldType,
} from '../../api-internal';
import { RecordGroup } from './CustomListRecordPage';
import { getValidationSchema, initInitialValues } from './helper';
import { customListRecordApi } from '../../apis';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { FormikErrors } from 'formik/dist/types';
import { CustomListRecordHeaderSubTitle } from './CustomListRecordHeaderSubTitle';
import UserInfo from './UserInfo';
import SalvPage from '../../common/SalvPage';

interface AddRecordFormValues {
  [key: string]: any;
}

interface Props {
  isNewRecord: boolean;
  customList: InternalApiCustomList | undefined;
  schemaFields: InternalApiCustomListSchemaField[];
  record: InternalApiCustomListRecord | undefined;
  setRecord?: (record: InternalApiCustomListRecord) => void;
  toggle: () => void;
  groupedFields: RecordGroup[];
}

const EditCustomListRecord = (props: Props) => {
  const navigate = useNavigate();

  const { isNewRecord, customList, schemaFields, record, toggle, groupedFields } = props;

  const initialValues = initInitialValues(schemaFields, record);

  const renderTitle = () => {
    if (isNewRecord) {
      return 'Add new record';
    } else {
      return `Edit Record ${record?.id || ''}`;
    }
  };

  const submit = async (
    values: AddRecordFormValues,
    { setSubmitting, resetForm }: FormikHelpers<AddRecordFormValues>
  ) => {
    try {
      setSubmitting(true);
      const { id, ...attributes } = values;
      if (record) {
        const record = await customListRecordApi.updateRecord({
          customListId: customList!.id!,
          id,
          internalApiCustomListRecord: {
            id,
            attributes,
          },
        });
        props.setRecord?.(record);
        toggle();
      } else {
        await customListRecordApi.addRecord({
          customListId: customList!.id!,
          internalApiCustomListRecord: {
            id: id ? id : uuidv4(),
            attributes,
          },
        });
        navigate('/custom-lists/' + customList?.id || '');
      }

      resetForm(initialValues);
    } catch (_) {
      //
    } finally {
      setSubmitting(false);
    }
  };

  const renderValueContainer = (
    schemaField: InternalApiCustomListSchemaField,
    value: any,
    setFieldValue: FormikHelpers<AddRecordFormValues>['setFieldValue'],
    isInvalid: boolean,
    withLabel = true,
    index?: number
  ) => {
    switch (schemaField.type) {
      case InternalApiCustomListSchemaFieldType.DATETIME:
        return (
          <>
            {withLabel && <ReactForm.Label className='mb-0'>{schemaField.displayName}</ReactForm.Label>}
            <div style={{ marginTop: '8px' }}>
              {value ? (
                <DatePicker
                  value={dayjs(value, 'YYYY-MM-DD')}
                  showDefaultOptions={false}
                  onChange={(value: Dayjs | null) =>
                    value
                      ? setFieldValue(schemaField.fieldName, value.format('YYYY-MM-DD'))
                      : setFieldValue(schemaField.fieldName, null)
                  }
                  isInvalid={isInvalid}
                />
              ) : (
                <DatePicker
                  value={null}
                  showDefaultOptions={false}
                  onChange={(value: Dayjs | null) =>
                    value && setFieldValue(schemaField.fieldName, value.format('YYYY-MM-DD'))
                  }
                  isInvalid={isInvalid}
                />
              )}
            </div>
          </>
        );
      case InternalApiCustomListSchemaFieldType.ENUM: {
        let dropdownValue: DropdownOption | DropdownOption[] | undefined;
        if (!value) {
          dropdownValue = undefined;
        } else if (schemaField.isMulti) {
          dropdownValue = value.map((option: string) => {
            return {
              key: option,
              value: option,
              label: option,
            };
          });
        } else {
          dropdownValue = {
            key: value,
            value: value,
            label: value,
          };
        }

        let onChange: (options: DropdownOption[]) => void;
        if (schemaField.isMulti) {
          onChange = (options) =>
            setFieldValue(
              schemaField.fieldName,
              options.map((option) => option.value)
            );
        } else {
          onChange = (options) => setFieldValue(schemaField.fieldName, options[0] ? options[0].value : undefined);
        }

        return (
          <SalvDropdown
            isMulti={schemaField.isMulti}
            label={withLabel ? schemaField.displayName : undefined}
            ariaLabel={withLabel ? undefined : schemaField.displayName + (Number.isInteger(index) ? index! + 1 : '')}
            options={schemaField.enumOptions!.map((option) => {
              return {
                key: option,
                value: option,
                label: option,
              };
            })}
            value={dropdownValue}
            isInvalid={isInvalid}
            onChange={onChange}
            clearable
          />
        );
      }
      default:
        return (
          <SalvTextArea
            label={withLabel ? schemaField.displayName : undefined}
            ariaLabel={withLabel ? undefined : schemaField.displayName + (Number.isInteger(index) ? index! + 1 : '')}
            value={value || ''}
            disabled={!!(schemaField.fieldName === 'id' && record)}
            isInvalid={isInvalid}
            onChange={(value) => setFieldValue(schemaField.fieldName, value)}
            autoSizing
            minRows={1}
          />
        );
    }
  };

  const displaySingleField = (
    schemaField: InternalApiCustomListSchemaField,
    values: AddRecordFormValues,
    setFieldValue: FormikHelpers<AddRecordFormValues>['setFieldValue'],
    isInvalid: boolean,
    index: number
  ) => {
    return (
      <Col key={index} md={4} className='mb-4'>
        <div className='row'>
          <div className='col'>
            {renderValueContainer(schemaField, values[schemaField.fieldName], setFieldValue, isInvalid)}
          </div>
        </div>
      </Col>
    );
  };

  const displaySingleObjectField = (
    singleObjectElement: InternalApiCustomListSchemaField,
    values: AddRecordFormValues,
    setFieldValue: FormikHelpers<AddRecordFormValues>['setFieldValue'],
    errors: FormikErrors<AddRecordFormValues> | undefined
  ) => {
    const childSchemaFields = schemaFields?.filter((schemaField) => schemaField.parentId === singleObjectElement.id);
    return (
      <Col md={12} className='mb-4' key={singleObjectElement.id}>
        <h4 style={{ marginBottom: '16px' }}>{singleObjectElement.displayName}</h4>
        <Table className='table-fixed mb-0' striped={true} hover={false} size='sm'>
          <thead>
            <tr>
              {childSchemaFields?.map((schemaField) => (
                <th key={schemaField.id}>{schemaField.displayName}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr
              style={{
                borderBottom: '1px solid #EDF2F9',
              }}
            >
              {childSchemaFields?.map((field) => {
                const value = values[singleObjectElement.fieldName]?.[field.fieldName] ?? null;

                return (
                  <td key={field.id}>
                    {renderValueContainer(
                      field,
                      value,
                      (field: string, value: any) => setFieldValue(`${singleObjectElement.fieldName}.${field}`, value),
                      !!errors?.[field.fieldName],
                      false
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </Table>
      </Col>
    );
  };

  const displayMultiField = (
    multiNonObjectElement: InternalApiCustomListSchemaField,
    values: AddRecordFormValues,
    setFieldValue: FormikHelpers<AddRecordFormValues>['setFieldValue'],
    errors: string[] | undefined
  ) => {
    return (
      <Col md={12} className='mb-4' key={multiNonObjectElement.id}>
        <h4 style={{ marginBottom: '16px' }}>{multiNonObjectElement.displayName}</h4>
        <Table className='table-fixed mb-3' responsive={true} striped={true} hover={false} size='sm'>
          <thead>
            <tr>
              <th>Values</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {values[multiNonObjectElement.fieldName]?.length > 0 &&
              values[multiNonObjectElement.fieldName].map((row: any, index: number) => {
                const removeRow = (index: number) => {
                  const newValues = [...values[multiNonObjectElement.fieldName]];
                  newValues.splice(index, 1);
                  setFieldValue(multiNonObjectElement.fieldName, newValues);
                };
                const updateRow = (field: any, value: any, index: number) => {
                  const newValues = [...values[multiNonObjectElement.fieldName]];
                  newValues[index] = value;
                  setFieldValue(multiNonObjectElement.fieldName, newValues);
                };
                return (
                  <tr
                    key={index}
                    style={{
                      borderBottom: '1px solid #EDF2F9',
                    }}
                  >
                    <td>
                      {renderValueContainer(
                        multiNonObjectElement,
                        row,
                        async (field: string, value: any) => updateRow(field, value, index),
                        !!errors?.[index],
                        false,
                        index
                      )}
                    </td>
                    <td className='text-end'>
                      <Button
                        variant='outline-danger'
                        size='medium'
                        onClick={() => {
                          removeRow(index);
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
        <Row className='mb-0 ms-3'>
          <Button
            variant='secondary'
            size='medium'
            onClick={() => {
              const value = values[multiNonObjectElement.fieldName] || [];
              const newElement = '';
              setFieldValue(multiNonObjectElement.fieldName, [...value, newElement]);
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add row
          </Button>
        </Row>
      </Col>
    );
  };

  const displayMultiObjectField = (
    multiObjectElement: InternalApiCustomListSchemaField,
    values: AddRecordFormValues,
    setFieldValue: FormikHelpers<AddRecordFormValues>['setFieldValue'],
    errors: FormikErrors<AddRecordFormValues>[] | undefined
  ) => {
    const childSchemaFields = schemaFields?.filter((schemaField) => schemaField.parentId === multiObjectElement.id);
    return (
      <Col md={12} className='mb-4' key={multiObjectElement.id}>
        <h4 style={{ marginBottom: '16px ' }}>{multiObjectElement.displayName}</h4>
        <Table
          key={multiObjectElement.id}
          className='table-fixed mb-3'
          responsive={true}
          striped={true}
          hover={false}
          size='sm'
        >
          <thead>
            <tr>
              {childSchemaFields?.map((schemaField) => (
                <th key={schemaField.id}>{schemaField.displayName}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {values[multiObjectElement.fieldName]?.length > 0 &&
              values[multiObjectElement.fieldName]?.map((row: any, index: number) => {
                const removeRow = (index: number) => {
                  const newValues = [...values[multiObjectElement.fieldName]];
                  newValues.splice(index, 1);
                  setFieldValue(multiObjectElement.fieldName, newValues);
                };
                const updateRow = (field: any, value: any, index: number) => {
                  const newValues = [...values[multiObjectElement.fieldName]];
                  newValues[index][field] = value;
                  setFieldValue(multiObjectElement.fieldName, newValues);
                };
                return (
                  <tr
                    key={index}
                    style={{
                      borderBottom: '1px solid #EDF2F9',
                    }}
                  >
                    {childSchemaFields?.map((schemaField) => (
                      <td key={schemaField.id}>
                        {renderValueContainer(
                          schemaField,
                          row[schemaField.fieldName],
                          async (field: string, value: any) => updateRow(field, value, index),
                          !!errors?.[index]?.[schemaField.fieldName],
                          false,
                          index
                        )}
                      </td>
                    ))}
                    <td className='text-end'>
                      <Button
                        variant='outline-danger'
                        size='medium'
                        onClick={() => {
                          removeRow(index);
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
        <Row className='mb-0 ms-3'>
          <Button
            variant='secondary'
            size='medium'
            onClick={() => {
              const value = values[multiObjectElement.fieldName] || [];
              const newElement = childSchemaFields?.reduce((acc, element) => {
                return {
                  ...acc,
                  [element.fieldName]: '',
                };
              }, {});
              setFieldValue(multiObjectElement.fieldName, [...value, newElement]);
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add row
          </Button>
        </Row>
      </Col>
    );
  };

  const displayFields = (
    group: RecordGroup,
    values: AddRecordFormValues,
    setFieldValue: FormikHelpers<AddRecordFormValues>['setFieldValue'],
    errors: FormikErrors<AddRecordFormValues>
  ) => {
    const { isObject, isMulti, type } = group.key;
    if ((!isObject && !isMulti) || type === InternalApiCustomListSchemaFieldType.ENUM) {
      return group.items.map((item, index) => {
        return displaySingleField(item, values, setFieldValue, !!errors[item.fieldName], index);
      });
    }
    if (isObject && !isMulti) {
      return group.items.map((item) =>
        displaySingleObjectField(
          item,
          values,
          setFieldValue,
          errors[item.fieldName] as FormikErrors<AddRecordFormValues> | undefined
        )
      );
    }
    if (!isObject && isMulti) {
      return group.items.map((item) =>
        displayMultiField(item, values, setFieldValue, errors[item.fieldName] as string[] | undefined)
      );
    }
    if (isObject && isMulti) {
      return group.items.map((item) =>
        displayMultiObjectField(
          item,
          values,
          setFieldValue,
          errors[item.fieldName] as FormikErrors<AddRecordFormValues>[] | undefined
        )
      );
    }
  };

  const validationSchema = useMemo(() => getValidationSchema(schemaFields), [schemaFields]);

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={submit} validationSchema={validationSchema}>
      {({ values, isSubmitting, setFieldValue, isValid, errors }: FormikProps<AddRecordFormValues>) => {
        return (
          <Form>
            <SalvPage
              title={renderTitle()}
              subTitle={<CustomListRecordHeaderSubTitle customList={customList} record={record} />}
              actions={
                <>
                  <SalvButton
                    variant='muted'
                    label='Cancel'
                    size={Size.SMALL}
                    onClick={() => (isNewRecord ? navigate(-1) : toggle())}
                  />
                  <SalvButton label='Save' size={Size.SMALL} type='submit' disabled={isSubmitting || !isValid} />
                </>
              }
              style={{
                overflowWrap: 'anywhere',
              }}
            >
              <Row>
                <>{groupedFields.map((group) => displayFields(group, values, setFieldValue, errors))}</>
              </Row>
              {record && (
                <>
                  <hr />
                  <UserInfo
                    description='Last updated by'
                    userName={record.lastUpdatedBy?.name}
                    time={record.lastUpdatedTime!}
                    className={'m-t-24'}
                  />
                  <UserInfo
                    description='Created by'
                    userName={record.recordCreatedBy?.name}
                    time={record.recordCreatedTime!}
                    className={'m-t-12'}
                  />
                </>
              )}
            </SalvPage>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditCustomListRecord;
